/// <reference types="@angular/localize" />

import { CommonModule, DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { DEFAULT_CURRENCY_CODE, LOCALE_ID, enableProdMode, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { NgxGdlApplicationModule, NgxGdlSharedModule } from '@krysgroup/ngx-application';
import { NgxGdlCommonModule } from '@krysgroup/ngx-common';
import { NgxGdlModalModule } from '@krysgroup/ngx-modal';
import { provideGdlNotification } from '@krysgroup/ngx-notification';
import { NgxGdlSimulatorModule } from '@krysgroup/ngx-simulator';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { QuillModule } from 'ngx-quill';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  LOCAL_STORAGE_KEY_APICONTEXT,
  LOCAL_STORAGE_KEY_CODE_VERIFIER,
  LOCAL_STORAGE_KEY_CONTEXT,
  LOCAL_STORAGE_KEY_REDIRECT,
  LOCAL_STORAGE_KEY_STATE,
  LOCAL_STORAGE_KEY_VERSIONS,
  LOCAL_STORAGE_KEY_WIDGET
} from './app/shared/constants/local-storage.constants';
import { WwwRouteConstants } from './app/shared/constants/www-route-constants';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

export const runner = (): void => {
  if (environment.production) {
    enableProdMode();
  }

  bootstrapApplication(
    AppComponent,
    {
      providers: [
        provideAnimations(),
        importProvidersFrom(
          NgbModule,
          CommonModule,
          NgxGdlModalModule,
          NgxGdlSharedModule,
          NgxGdlApplicationModule.forRoot({
            applicationName: environment.applicationName,
            environmentName: environment.name,
            environmentVersion: environment.version,
            angularUrl: environment.angularUrl,
            disableBetaRedirect: environment.disableBetaRedirect,
            localStorageKey: {
              contextKey: LOCAL_STORAGE_KEY_CONTEXT,
              apiContextKey: LOCAL_STORAGE_KEY_APICONTEXT,
              redirectKey: LOCAL_STORAGE_KEY_REDIRECT,
              stateKey: LOCAL_STORAGE_KEY_STATE,
              codeVerifierKey: LOCAL_STORAGE_KEY_CODE_VERIFIER,
              widgetKey: LOCAL_STORAGE_KEY_WIDGET,
              versionKey: LOCAL_STORAGE_KEY_VERSIONS
            },
            sso: {
              clientId: environment.SSO.clientId,
              path: environment.SSO.path
            },
            wwwRoute: {
              secure: WwwRouteConstants.SECURE,
              home: WwwRouteConstants.REDIRECT,
              apiContext: WwwRouteConstants.APICONTEXT
            }
          }),
          NgxGdlCommonModule.forRoot({
            isConsoleEnabled: environment.production === true,
            localStorageAesKey: environment.localStorageAesKey,
            encryptStorage: environment.encryptStorage,
            webApiUrl: environment.apiUrl,
            apiUrlPrefix: environment.apiUrlPrefix,
            localStorageKeyApiContext: LOCAL_STORAGE_KEY_APICONTEXT
          }),
          NgxGdlSimulatorModule.forRoot({
            useSimulation: environment.useSimulation === true
          }),
          QuillModule.forRoot(),
          TranslateModule.forRoot({
            loader: {
              provide: TranslateLoader,
              useClass: TranslateHttpLoader
            }
          })),
        ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
        {
          provide: LOCALE_ID, useValue: 'fr-FR'
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(appRoutes),
        { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'shortDate' } },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        provideGdlNotification()
      ]
    })
    .catch((err: any) => console.error(err));
};

registerLocaleData(localeFr);

fetch('/assets/config/config.json')
  .then((response: Response) => response.json())
  .then((config: Configuration) => {
    environment.production = config.environment.production === 'true';
    environment.name = config.environment.name;

    environment.applicationName = config.application.name;
    environment.apiUrl = config.application.apiUrl;
    environment.apiUrlPrefix = config.application.apiUrlPrefix;
    environment.angularUrl = config.application.angularUrl;
    environment.localStorageAesKey = config.application.localStorageAesKey;
    environment.encryptStorage = config.application.encryptStorage === 'true';
    environment.useSimulation = config.application.useSimulation === 'true';
    environment.version = config.application.version;
    environment.SSO = {
      path: config.SSO.authorizationServer,
      wsPath: config.SSO.resourceServer,
      clientId: config.SSO.clientId,
      unikPath: config.SSO.unikPath
    };

    runner();
  });

export interface Configuration {
  environment: Environment;
  application: Application;
  SSO: SSO;
}
export interface Environment {
  production: string;
  name: string;
}
export interface Application {
  name: string;
  apiUrl: string;
  apiUrlPrefix: string;
  angularUrl: string;
  localStorageAesKey: string;
  encryptStorage: string;
  version: string;
  useSimulation: string;
}
export interface SSO {
  authorizationServer: string;
  resourceServer: string;
  clientId: string;
  unikPath: string;
}
